import React from 'react';
import { groupBy, RenderLabelAssetProps, LabelsPopoverContext, LabelButtonContext } from '../LabelHelpers';
import { LabelAssetChildGroupDiv, LabelAssetGroupDiv, LabelGroupHeadingDiv, LabelLabelsGroupDiv } from '../Styles';
import { onThisAssetMessage } from '../shared/LabelAssetContent';
import { ExpandoWidgetWrapper, LabelDetailAuthorElement, LabelDetailDateElement, LabelDetailMessageElement, TextLinkWidgetLabelDetailAsset } from '../shared/LabelPopoverDetail';
import { LabelType } from '../shared/LabelType';
import { HydratedCertificationRenderProps, RenderCertification, RenderParentedCertification } from './CertificationProps';

function CertificationAnchoredSection({ anchored, ...props }: Omit<HydratedCertificationRenderProps, 'parented' | 'upstream'>): JSX.Element | null {
  if (!anchored || anchored.labels.length < 1) {
    return null;
  }

  const { contentType } = props.content;
  return (
    <>
      <LabelGroupHeadingDiv>
        {onThisAssetMessage(contentType)}
      </LabelGroupHeadingDiv>
      <ExpandoWidgetWrapper
        length={anchored?.labels.length}
        labelType={LabelType[LabelType.CERTIFIED]}
      >
        <RenderAnchoredAssetsCertifications anchored={anchored} {...props} />
      </ExpandoWidgetWrapper>
    </>
  )
}

function RenderAnchoredAssetsCertifications({ anchored, ...props }: Omit<HydratedCertificationRenderProps, 'parented' | 'upstream'>): JSX.Element | null {
  const { linksDisabled } = React.useContext(LabelButtonContext);
  if (!anchored?.labels || anchored?.labels.length < 1) {
    return null;
  }
  const { contentName, contentType, contentUrl, remoteType } = props.content;
  return (
    <>
      <LabelLabelsGroupDiv>
        <TextLinkWidgetLabelDetailAsset
          assetName={contentName ?? null}
          assetType={contentType ?? null}
          assetUrl={linksDisabled ? null : (contentUrl ?? null)}
          remoteType={remoteType ?? undefined}
        />
        <RenderCertifications assetName={contentName ?? null} certifications={anchored?.labels} />
      </LabelLabelsGroupDiv>
    </>
  )
}

function RenderCertifications(props: { readonly assetName: string | null, certifications: readonly RenderCertification[] } & { readonly keyIndex?: string; }): JSX.Element {
  const { category } = React.useContext(LabelsPopoverContext);
  const { assetName, certifications } = props;
  return (
    <React.Fragment key={props.keyIndex}>
      {
        certifications.map((i, index) =>
          <React.Fragment key={index}>
            <span style={{ display: 'flex', }}>
              <LabelDetailAuthorElement {...i} category={category} />
            </span>
            <LabelDetailDateElement {...i} />
            <LabelDetailMessageElement {...i} assetName={assetName} />
          </React.Fragment>
        )
      }
    </React.Fragment>
  )
}

function CertificationParentedSection({ parented }: Omit<HydratedCertificationRenderProps, 'anchored'>): JSX.Element | null {
  if (!parented || parented.labels.length < 1) {
    return null;
  }
  const copy = parented.labels.map(i => i);
  const labelsMap: Map<string, RenderParentedCertification[]> = groupBy(copy, i => i.content?.contentId ?? '');

  const groupedAssetLabels: RenderLabelAssetProps[] = []

  labelsMap.forEach((value: RenderParentedCertification[]) => groupedAssetLabels.push({
    subjectArea: value[0].content?.subjectArea ?? null,
    assetName: value[0].content?.contentName ?? null,
    assetType: value[0].content?.contentType ?? null,
    assetUrl: value[0].content?.contentUrl ?? null,
    remoteType: value[0].content?.remoteType ?? undefined,
    category: 'certification',
    labels: value
  }));

  return (
    <LabelAssetChildGroupDiv>
      {
        groupedAssetLabels.map((c, i) => {
          return (
            <LabelAssetGroupDiv key={i}>
              <TextLinkWidgetLabelDetailAsset
                assetName={c.assetName}
                assetType={c.assetType}
                assetUrl={c.assetUrl}
                remoteType={c.remoteType}
              />
              <LabelLabelsGroupDiv>
                <RenderCertifications assetName={c.assetName ?? null} certifications={c.labels} keyIndex={`${i}`} />
              </LabelLabelsGroupDiv>
            </LabelAssetGroupDiv>)
        })
      }
    </LabelAssetChildGroupDiv>
  );
}

export function CertificationPopover(props: HydratedCertificationRenderProps): JSX.Element {
  return (props.anchored ? <CertificationAnchoredSection {...props} /> : <CertificationParentedSection {...props} />);
}
